import axios from 'axios';
import { Handles } from 'model/meeting';
import { v4 as uuidv4 } from 'uuid';
import {
  setCompanyId, setStaffId, setUserId, setMobile,
  setUserToken
} from '../data/storage';

interface UserInfo {
  userId: string;
  mobile: string;
  companyId: string;
  companyName: string;
  deleted: number;
  staffId: string;
}

interface CoMeetingMsg {
  key: Handles;
  value?: string | number | boolean | Object;
}

type onMsgCallback = (msg: CoMeetingMsg) => void;

const CoSDK = window && window.MAXHUB_WORK_SDK;

class CooperationSDK {
  private _isCoEnv = false;
  private _broadcastChannel: BroadcastChannel | undefined;
  private _bcListeners: onMsgCallback[] = [];
  private _isInited = false;

  constructor () {
    this._isCoEnv = this.detectCoEnv();
    this.init();
  }

  // 获取用户信息
  async fetchUserInfo (): Promise<any> {
    try {
      await this.init();
      const info = await this.request('get-local-state', { name: 'currentStaffInfo' }) as UserInfo;

      const { mobile, userId, companyId, staffId } = info || {};

      setCompanyId(companyId);
      setStaffId(staffId);
      setUserId(userId);
      setMobile(mobile);

      console.log('[fetchUserInfo success]');
      return info;
    } catch (e) {
      console.error('[fetchUserInfo error]', e);
      throw e;
    }
  }

  // 获取用户token
  async fetchUserToken (): Promise<any> {
    try {
      await this.init();
      const token = await this.request('get-user-token') as any;
      setUserToken(token.access_token);
      console.log('[fetchUserToken success]');
      return token;
    } catch (e) {
      console.error('[fetchUserToken error]', e);
      throw e;
    }
  };

  // 检查用户信息是否有效
  isUserInfoValid (info: UserInfo): boolean {
    const { userId, companyId, staffId } = info;
    return !!userId && !!companyId && !!staffId;
  };

  async createWindow ({ id = uuidv4(), url = '/', resizable = true, customScript = '', minWidth = 1280, minHeight = 688 }): Promise<void> {
    console.log('[create-co-window], url:', url);
    await this.request('create-window', { id, url, resizable, customScript, width: minWidth, height: minHeight, minWidth, minHeight });
  };

  async getState (key: string): Promise<any> {
    const config = await this.request('get-state', { name: key }) as any;
    return config;
  };

  async destoryWindow (): Promise<any> {
    return await this.request('destroy-window');
  };

  // 打开网站用默认浏览器
  async openExternal (url: string): Promise<void> {
    if (this.isCoEnv) {
      await this.request('open-external', { url });
    }
  };

  postBCMessage (msg: CoMeetingMsg): void {
    if (!this.isCoEnv || !this._broadcastChannel) return;
    this._broadcastChannel.postMessage(msg);
  };

  onBCMessage = (cb: onMsgCallback) => {
    if (!this.isCoEnv || !this._broadcastChannel) return;
    this._bcListeners.push(cb);
  };

  private detectCoEnv (): boolean {
    return !!window && !!window.MAXHUB_WORK_SDK;
  }

  // 获取webapp授权
  private async fetchWeappAuth () {
    try {
      const { data } = await axios.get(`/api-pivot/api/client/webappAuth/getCode?codeId=${uuidv4()}`);
      return data.data;
    } catch (e) {
      console.error('获取webapp授权失败', e);
      throw e;
    }
  }

  private async request (attributes: string, config: any = {}) {
    if (!this.isCoEnv) return Promise.reject(new Error('MAXHUB_WORK_SDK not found!'));
    return new Promise((resolve, reject) => {
      CoSDK.request(attributes, config, (err: Error, res: any) => {
        if (err) {
          return reject(err);
        }
        return resolve(res);
      });
    });
  }

  private async init (): Promise<void> {
    if (!this.isCoEnv || this._isInited) return;

    const auth = await this.fetchWeappAuth();

    CoSDK.init(auth);
    this.initBroadcastChannel();

    this._isInited = true;
  }

  private initBroadcastChannel () {
    if (!this.isCoEnv || !window.BroadcastChannel) return;
    this._broadcastChannel = new BroadcastChannel('co_meeting_notification');
    this._broadcastChannel.onmessage = (ev) => {
      this._bcListeners.forEach(cb => cb(ev.data));
    };
  }

  get isCoEnv (): boolean {
    return this._isCoEnv;
  }
}

const cooperationSDK = new CooperationSDK();

export default cooperationSDK;
